import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Nullable,
  UserWithToken,
  WithData,
  WithMeta,
  StoreLinkType,
} from '@ygoargentina/sdk/api/api.types';
import z from 'zod';
import axiosClient from './request-client';
import { AxiosRequestConfig } from 'axios';

export type ContentCreatorLink = {
  id: number;
  contentCreatorId: number;
  type: StoreLinkType;
  url: string;
  label: Nullable<string>;
  customIcon: Nullable<string>;
};

export type ContentCreator = {
  id: number;
  name: string;
  logoUrl: Nullable<string>;
  description: Nullable<string>;
  tags: Nullable<string[]>;
  links: ContentCreatorLink[];
  createdAt: string;
  updatedAt: string;
  deletedAt: Nullable<string>;
};

export const contentCreatorDetailSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

export const contentCreatorLinkSchema = z.object({
  type: z.string(),
  url: z.string(),
  label: z.string().optional(),
  customIcon: z.string().optional(),
});

export const getPublicContentCreators = async (
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(null).request<WithMeta<ContentCreator>>(
    gql`
      query publicContentCreators($filters: ContentCreatorFilters) {
        data: publicContentCreators(filters: $filters) {
          data {
            id
            name
            logoUrl
            description
            tags

            links {
              id
              type
              url
              label
              customIcon
            }
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters: variables }
  );

export const listContentCreators = async (
  user: Nullable<UserWithToken>,
  variables?: Record<string, unknown>
) =>
  getGraphQLQueryClient(user).request<WithMeta<ContentCreator>>(
    gql`
      query contentCreators($filters: ContentCreatorFilters) {
        data: contentCreators(filters: $filters) {
          data {
            id
            name
            logoUrl
            description
            tags

            links {
              id
              type
              url
              label
              customIcon
            }
          }
          meta {
            total
            perPage
            lastPage
          }
        }
      }
    `,
    { filters: variables }
  );

export const getContentCreator = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<ContentCreator>>(
    gql`
      query contentCreator($id: Int!) {
        data: contentCreator(id: $id) {
          id
          name
          logoUrl
          description
          tags

          links {
            id
            type
            url
            label
            customIcon
          }
          createdAt
          updatedAt
        }
      }
    `,
    { id }
  );

export const createContentCreator = async (
  user: Nullable<UserWithToken>,
  input: z.infer<typeof contentCreatorDetailSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<ContentCreator>>(
    gql`
      mutation createContentCreator($input: ContentCreatorInput!) {
        data: createContentCreator(input: $input) {
          id
          name
        }
      }
    `,
    { input }
  );

export const updateContentCreator = async (
  user: Nullable<UserWithToken>,
  id: number,
  input: z.infer<typeof contentCreatorDetailSchema>
) =>
  getGraphQLQueryClient(user).request<WithData<ContentCreator>>(
    gql`
      mutation updateContentCreator($id: Int!, $input: ContentCreatorInput!) {
        data: updateContentCreator(id: $id, input: $input) {
          id
          name
          logoUrl
          description
          tags

          links {
            id
            type
            url
            label
            customIcon
          }
        }
      }
    `,
    { id, input }
  );

export const deleteContentCreator = async (
  user: Nullable<UserWithToken>,
  id: number
) =>
  getGraphQLQueryClient(user).request<WithData<ContentCreator>>(
    gql`
      mutation deleteContentCreator($id: Int!) {
        data: deleteContentCreator(id: $id) {
          id
        }
      }
    `,
    { id }
  );

export const uploadContentCreatorLogo = (
  contentCreatorId: number,
  file: File,
  config?: AxiosRequestConfig
) => {
  const formData = new FormData();
  formData.append('logo', file);

  return axiosClient.post(
    `/content-creators/${contentCreatorId}/logo`,
    formData,
    {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

export const deleteContentCreatorLogo = (
  contentCreatorId: number,
  config?: AxiosRequestConfig
) => {
  return axiosClient.delete(
    `/content-creators/${contentCreatorId}/logo`,
    config
  );
};
